import React, { forwardRef } from 'react';
import { clsx } from 'clsx';
import { ChevronDown } from 'lucide-react';

interface SelectOption {
  value: string;
  label: string;
}

interface SelectProps extends Omit<React.SelectHTMLAttributes<HTMLSelectElement>, 'children'> {
  label?: string;
  error?: string;
  helperText?: string;
  options: SelectOption[];
  placeholder?: string;
  /**
   * Class names applied to the outer wrapper. When omitted, sizing utilities
   * (w-*, max-w-*, min-w-*, flex-*, basis-*) detected in `className` are
   * forwarded to the wrapper so existing call sites that pass widths via
   * `className` keep working.
   */
  wrapperClassName?: string;
}

const SIZE_PATTERN = /^(?:(?:sm|md|lg|xl|2xl):)?(?:w-|max-w-|min-w-|flex-(?:1|auto|none|grow|shrink)|basis-)/;

function splitSizeClasses(cn?: string): { size: string; rest: string } {
  if (!cn) return { size: '', rest: '' };
  const sizeTokens: string[] = [];
  const restTokens: string[] = [];
  for (const token of cn.split(/\s+/).filter(Boolean)) {
    (SIZE_PATTERN.test(token) ? sizeTokens : restTokens).push(token);
  }
  return { size: sizeTokens.join(' '), rest: restTokens.join(' ') };
}

const Select = forwardRef<HTMLSelectElement, SelectProps>(
  ({ label, error, helperText, options, placeholder, className, wrapperClassName, ...props }, ref) => {
    const { size, rest } = splitSizeClasses(className);
    const wrapperCls = wrapperClassName ?? (size || 'w-full');
    return (
      <div className={wrapperCls}>
        {label && (
          <label className="label">
            {label}
            {props.required && <span className="text-red-500 ml-1">*</span>}
          </label>
        )}
        <div className="relative">
          <select
            ref={ref}
            className={clsx(
              'input appearance-none pr-10',
              error && 'input-error',
              rest
            )}
            {...props}
          >
            {placeholder && (
              <option value="" disabled>
                {placeholder}
              </option>
            )}
            {options.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none text-gray-400">
            <ChevronDown className="w-4 h-4" />
          </div>
        </div>
        {error && (
          <p className="mt-1 text-sm text-red-600 dark:text-red-400">{error}</p>
        )}
        {helperText && !error && (
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{helperText}</p>
        )}
      </div>
    );
  }
);

Select.displayName = 'Select';

export { Select };
export default Select;
